import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../service/firebase';
import { signInWithEmailAndPassword } from "firebase/auth";
import api from '../service/api';
import { Eye, EyeOff, Loader2, Clock, Mail, Lock } from 'lucide-react'; // Loader2: carregamento; Clock: modal de inatividade; Mail/Lock: ícones dos campos
import '../css/style.css';
import '../css/Login.css';

function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    
    const [loading, setLoading] = useState(false);
    const [showExpiredModal, setShowExpiredModal] = useState(false); // CAP-6: modal de sessão encerrada por inatividade

    const navigate = useNavigate();
    const location = useLocation();

    // CAP-6: redirect por expiração de sessão exibe um modal de inatividade (não apenas texto inline).
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('expired') === 'true') {
            setShowExpiredModal(true);
            // Limpa o parâmetro da URL para que um refresh não reexiba o modal.
            window.history.replaceState({}, '', '/');
        }
    }, [location.search]);

    const handleLogin = async () => {
        if (!email || !password) {
            setErrorMessage("Preencha todos os campos.");
            return;
        }

        setLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const idToken = await userCredential.user.getIdToken();

            const response = await api.post('/api/login', { idToken }, { withCredentials: true });
            const data = response.data.userData;
            localStorage.setItem('@AppAcademia:user', JSON.stringify(data));

            if (data?.colorStyle) {
                document.documentElement.setAttribute('data-color-style', data.colorStyle);
            } else {
                document.documentElement.removeAttribute('data-color-style');
            }
            if (data?.colorTheme) {
                document.documentElement.setAttribute('data-theme', data.colorTheme);
            } else {
                document.documentElement.removeAttribute('data-theme');
            }

            navigate("/home");
        } catch (error) {
            console.error(error);
            switch (error.code) {
                case "auth/invalid-credential":
                    setErrorMessage("E-mail ou senha inválidos.");
                    break;
                case "auth/invalid-email":
                    setErrorMessage("E-mail inválido.");
                    break;
                default:
                    setErrorMessage("Erro ao entrar. Tente novamente.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setErrorMessage("");
    }, [email, password]);

    return (
        <div className="login-container">
            {showExpiredModal && (
                <div className="session-modal-overlay">
                    <div className="session-modal">
                        <div className="session-modal-icon">
                            <Clock size={48} />
                        </div>
                        <h2 className="session-modal-title">Sessão encerrada</h2>
                        <p className="session-modal-text">
                            Sua sessão foi encerrada por inatividade. Faça login novamente para continuar.
                        </p>
                        <button
                            className="session-modal-button"
                            onClick={() => setShowExpiredModal(false)}
                        >
                            Entendi
                        </button>
                    </div>
                </div>
            )}
            <div className="login-content">
                <div className="login-block">
                    {/* Espaço reservado para a logo PersonalMetrics */}
                    <div className="login-topo-espaco"></div>

                    <div className="login-form-area">
                        <h1>Bem-vindo(a)!</h1>
                        <h3>Acesse sua conta e transforme dados em resultados.</h3>

                        <div className="form-login">
                            <label htmlFor="user">E-mail</label>
                            <div className="login-campo">
                                <span className="login-campo-icone"><Mail size={18} /></span>
                                <input
                                    className="inputs-login"
                                    id="user"
                                    type="email"
                                    placeholder="seu@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>

                            <label htmlFor="pass">Senha</label>
                            <div className="login-campo">
                                <span className="login-campo-icone"><Lock size={18} /></span>
                                <input
                                    id="pass"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Sua senha"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="inputs-login"
                                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>

                            <div className="login-opcoes">
                                <a
                                    className="login-recuperar"
                                    onClick={() => navigate('/recuperar-senha')}
                                >
                                    Esqueceu sua senha?
                                </a>
                            </div>

                            <button
                                onClick={handleLogin}
                                className="login-button"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="loader-content">
                                        <Loader2 className="animate-spin" size={18} /> Carregando...
                                    </span>
                                ) : (
                                    "Entrar"
                                )}
                            </button>
                        </div>

                        {errorMessage && <p className="error-message">{errorMessage}</p>}
                    </div>

                    <div className="login-rodape">
                        <span className="login-rodape-linha"></span>
                        <div className="login-rodape-texto">
                            <p className="login-rodape-titulo">DADOS . ANÁLISE . PRESCRIÇÃO . RESULTADOS .</p>
                            <p className="login-rodape-sub">A TECNOLOGIA QUE TRANSFORMA PROFISSIONAIS EM REFERÊNCIA.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;